import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SubscriptionInfo {
  subscribed: boolean;
  isAdmin: boolean;
  planType: string | null;
  statusDetail: string | null;
  subscriptionEnd: string | null;
  status: 'idle' | 'checking' | 'ready' | 'error';
  error: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  subscription: SubscriptionInfo;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  checkSubscription: () => Promise<void>;
  retrySubscriptionCheck: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const ADMIN_EMAIL = 'psi.mariliateixeira@gmail.com';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionInfo>({
    subscribed: false,
    isAdmin: false,
    planType: null,
    statusDetail: null,
    subscriptionEnd: null,
    status: 'idle',
    error: null,
  });

  const checkInProgress = useRef(false);
  const hasCheckedOnce = useRef(false);

  const checkSubscription = useCallback(async (): Promise<void> => {
    if (hasCheckedOnce.current) return;
    if (checkInProgress.current) return;

    const { data: { session: freshSession } } = await supabase.auth.getSession();
    if (!freshSession) {
      setSubscription(prev => ({ ...prev, status: 'ready' }));
      return;
    }

    checkInProgress.current = true;
    setSubscription(prev => ({ ...prev, status: 'checking', error: null }));

    try {
      const userId = freshSession.user.id;
      const userEmail = freshSession.user.email?.toLowerCase();

      // Admin check by email (permanent access)
      if (userEmail === ADMIN_EMAIL) {
        hasCheckedOnce.current = true;
        setSubscription({
          subscribed: true,
          isAdmin: true,
          planType: 'admin',
          statusDetail: 'active',
          subscriptionEnd: null,
          status: 'ready',
          error: null,
        });
        checkInProgress.current = false;
        return;
      }

      // Independent checks run together instead of serial network round trips.
      const [{ data: roleData }, { data: subData, error: subError }] = await Promise.all([
        supabase.from('user_roles').select('role').eq('user_id', userId).eq('role', 'admin').maybeSingle(),
        supabase.from('subscriptions').select('status, plan_type, current_period_end').eq('user_id', userId).maybeSingle(),
      ]);

      if (roleData) {
        hasCheckedOnce.current = true;
        setSubscription({
          subscribed: true,
          isAdmin: true,
          planType: 'admin',
          statusDetail: 'active',
          subscriptionEnd: null,
          status: 'ready',
          error: null,
        });
        checkInProgress.current = false;
        return;
      }

      if (subError) {
        throw new Error(subError.message);
      }

      hasCheckedOnce.current = true;
      const isActive = subData?.status === 'active';
      
      setSubscription({
        subscribed: isActive,
        isAdmin: false,
        planType: subData?.plan_type ?? null,
        statusDetail: subData?.status ?? 'inactive',
        subscriptionEnd: subData?.current_period_end ?? null,
        status: 'ready',
        error: null,
      });
    } catch (error) {
      console.error('[Auth] Subscription check error:', error);
      hasCheckedOnce.current = true;
      setSubscription(prev => ({
        ...prev,
        status: 'error',
        error: 'Erro ao verificar assinatura. Tente novamente.',
      }));
    }

    checkInProgress.current = false;
  }, []);

  const retrySubscriptionCheck = useCallback(() => {
    hasCheckedOnce.current = false;
    checkInProgress.current = false;
    setSubscription(prev => ({ ...prev, status: 'idle' }));
    void checkSubscription();
  }, [checkSubscription]);

  useEffect(() => {
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setLoading(false);

        if (event === 'SIGNED_IN' && newSession) {
          hasCheckedOnce.current = false;
          checkInProgress.current = false;
          setSubscription(prev => ({ ...prev, status: 'idle' }));
        } else if (event === 'SIGNED_OUT') {
          hasCheckedOnce.current = false;
          checkInProgress.current = false;
          setSubscription({
            subscribed: false,
            isAdmin: false,
            planType: null,
            statusDetail: null,
            subscriptionEnd: null,
            status: 'idle',
            error: null,
          });
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setLoading(false);
    });

    return () => {
      authSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (session && !hasCheckedOnce.current && !checkInProgress.current) {
      void checkSubscription();
    }
  }, [session, checkSubscription]);

  const signUp = async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: { full_name: fullName },
      },
    });
    if (error) throw error;
    toast.success('Conta criada com sucesso! Você já pode fazer login.');
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    toast.success('Login realizado com sucesso!');
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    toast.success('Logout realizado com sucesso!');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        subscription,
        signUp,
        signIn,
        signOut,
        checkSubscription,
        retrySubscriptionCheck,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
